/** RecordingSaveForm — collect metadata after a recording completes.
 *
 *  Fields: title, matter (optional), meeting type, is_key_meeting flag.
 *  On submit, the parent creates the Meeting row, then uploads the audio
 *  blob via uploadAudio(). The form runs entirely client-side; network
 *  state is delegated to the parent via the `submitting` prop so the parent
 *  owns the order of the two network calls and the error surface.
 */

import { useState } from "react";
import { Button, Form, Input, Select, Switch, Empty } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import type { CompassItem } from "../../types";
import {
  MEETING_TYPE_OPTIONS,
  type MeetingType,
} from "../../types/meeting";
import styles from "../../styles/recording.module.css";

export interface RecordingSavePayload {
  title: string;
  matterId: string | null;
  meetingType: MeetingType | null;
  isKeyMeeting: boolean;
}

interface RecordingSaveFormProps {
  /** Recent matters from useCompass() — the same shortlist AIInputBar uses. */
  matters: CompassItem[];
  /** Default title suggestion, e.g. derived from current time. */
  defaultTitle?: string;
  submitting?: boolean;
  submitLabel?: string;
  onCancel: () => void;
  onSubmit: (payload: RecordingSavePayload) => void;
}

export default function RecordingSaveForm({
  matters,
  defaultTitle,
  submitting,
  submitLabel = "保存录音",
  onCancel,
  onSubmit,
}: RecordingSaveFormProps) {
  const [title, setTitle] = useState(defaultTitle ?? "");
  const [matterId, setMatterId] = useState<string | null>(null);
  const [meetingType, setMeetingType] = useState<MeetingType | null>(
    "client_consultation",
  );
  const [isKeyMeeting, setIsKeyMeeting] = useState(false);

  const canSubmit = title.trim().length > 0 && !submitting;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      title: title.trim(),
      matterId,
      meetingType,
      isKeyMeeting,
    });
  };

  const matterOptions = matters
    .filter((m) => m.type === "matter")
    .map((m) => ({
      value: m.id,
      label: m.clientName
        ? `${m.clientName} · ${m.title}`
        : m.title,
    }));

  return (
    <div className={styles.saveForm}>
      <Form layout="vertical" requiredMark>
        <Form.Item label="标题" required>
          <Input
            placeholder="例：客户初次会谈"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            autoFocus
            onPressEnter={handleSubmit}
          />
        </Form.Item>

        <Form.Item label="关联事项">
          <Select
            allowClear
            placeholder="选择关联的客户与事项（可选）"
            value={matterId ?? undefined}
            onChange={(v) => setMatterId(v ?? null)}
            notFoundContent={
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="近期无活跃事项"
              />
            }
            options={matterOptions}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>

        <Form.Item label="会议类型">
          <Select<MeetingType>
            placeholder="选择类型"
            value={meetingType ?? undefined}
            onChange={(v) => setMeetingType(v ?? null)}
            allowClear
            options={MEETING_TYPE_OPTIONS}
          />
        </Form.Item>

        <Form.Item label="标记为关键会议">
          <div className={styles.keyMeetingRow}>
            <Switch
              checked={isKeyMeeting}
              onChange={setIsKeyMeeting}
              aria-label="标记为关键会议"
            />
            <span className={styles.keyMeetingHint}>
              标记后该录音将在事项详情页突出展示
            </span>
          </div>
        </Form.Item>

        <div className={styles.saveActions}>
          <Button onClick={onCancel} disabled={submitting}>
            取消
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={submitting}
          >
            {submitLabel}
          </Button>
        </div>
      </Form>
    </div>
  );
}
